import React, { useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import Slide from "@material-ui/core/Slide";
import { withStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import FormControl from "@material-ui/core/FormControl";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = theme => ({
  appBar: {
    position: "relative"
  },
  title: {
    marginLeft: "2rem",
    flex: 1,
    color: "white"
  },
  tableCell: {
    padding: "8px 4px 8px 4px",
    border: "1px solid #dee2e6"
  }
});

function getBudgetLabel(budget) {
  if (!budget) return "";
  const provider = budget.provider ? budget.provider.business_name : "";
  const version = budget.version !== undefined ? budget.version : "";
  return `${provider} - v${version}`;
}

const CompareBudgets = ({ classes, budgets = [], open, onClose }) => {
  const [leftId, setLeftId] = useState("");
  const [rightId, setRightId] = useState("");

  const leftBudget = useMemo(
    () => budgets.find(b => String(b.id) === String(leftId)),
    [budgets, leftId]
  );
  const rightBudget = useMemo(
    () => budgets.find(b => String(b.id) === String(rightId)),
    [budgets, rightId]
  );

  const allTaskNames = useMemo(() => {
    const names = new Set();
    if (leftBudget && Array.isArray(leftBudget.tasks)) {
      leftBudget.tasks.forEach(t => {
        if (t && t.task && t.task.name) names.add(t.task.name);
      });
    }
    if (rightBudget && Array.isArray(rightBudget.tasks)) {
      rightBudget.tasks.forEach(t => {
        if (t && t.task && t.task.name) names.add(t.task.name);
      });
    }
    return Array.from(names.values()).sort();
  }, [leftBudget, rightBudget]);

  const getTaskRowValues = (budget, taskName) => {
    const parseMoney = v => {
      if (v === null || v === undefined) return 0;
      if (typeof v === "number") return v;
      const n = parseFloat(String(v).replace(/[$,\s,]/g, ""));
      return isNaN(n) ? 0 : n;
    };
    if (!budget || !Array.isArray(budget.tasks)) return { workforce: 0, materials: 0, total: 0 };
    const item = budget.tasks.find(t => t && t.task && t.task.name === taskName);
    if (!item) return { workforce: 0, materials: 0, total: 0 };
    const workforce = parseMoney(item.workforce_s_iva_price || 0);
    const materials = parseMoney(item.materials_s_iva_price || 0);
    const total = parseMoney(
      item.total_s_iva_price !== undefined ? item.total_s_iva_price : workforce + materials
    );
    return { workforce, materials, total };
  };

  const Summary = ({ budget, align }) => (
    <div style={{ minWidth: 280 }}>
      <Typography variant="subtitle2">
        {getBudgetLabel(budget)}
      </Typography>
      <Typography variant="caption" align={align}>
        Estado: {budget ? budget.state : "-"}
      </Typography>
      <div>
        <Typography variant="caption" align={align}>
          Mano de obra S/IVA: ${budget ? (budget.total_workforce || 0) : 0}
        </Typography>
      </div>
      <div>
        <Typography variant="caption" align={align}>
          Repuestos S/IVA: ${budget ? (budget.total_materials || 0) : 0}
        </Typography>
      </div>
      <div>
        <Typography variant="caption" align={align}>
          Total S/IVA: ${budget ? (budget.total_budget || budget.total_s_iva_price || 0) : 0}
        </Typography>
      </div>
    </div>
  );

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>Comparar presupuestos</Typography>
        </Toolbar>
      </AppBar>
      <div className="row mt-3 ml-4 mr-4">
        <div className="col">
          <FormControl style={{ minWidth: 260 }}>
            <Select
              native
              value={leftId}
              onChange={e => setLeftId(e.target.value)}
              style={{
                fontSize: '0.75rem'
              }}
              input={<Input id="left-budget-select" />}
            >
              <option value="">Seleccione presupuesto A</option>
              {budgets.map(b => (
                <option key={b.id} value={b.id}>{getBudgetLabel(b)}</option>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="col">
          <FormControl style={{ minWidth: 260 }}>
            <Select
              native
              value={rightId}
              onChange={e => setRightId(e.target.value)}
              style={{
                fontSize: '0.75rem'
              }}
              input={<Input id="right-budget-select" />}
            >
              <option value="">Seleccione presupuesto B</option>
              {budgets.map(b => (
                <option key={b.id} value={b.id}>{getBudgetLabel(b)}</option>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="row mt-3 ml-4 mr-4">
        <div className="col">
          <Summary budget={leftBudget} align="left" />
        </div>
        <div className="col">
          <Summary budget={rightBudget} align="right" />
        </div>
      </div>

      <div className="row mt-3 ml-4 mr-4">
        <Table size="small" aria-label="compare table" style={{ width: "100%", minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Tarea</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>MO A</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Repuestos A</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Total A</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>&nbsp;</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>MO B</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Repuestos B</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Total B</TableCell>
              <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Diferencia Total</TableCell>
               <TableCell className={classes.tableCell} style={{ fontWeight: "550" }}>Dif. %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allTaskNames.map(name => {
              const a = getTaskRowValues(leftBudget, name);
              const b = getTaskRowValues(rightBudget, name);
              const diff = Number((a.total - b.total).toFixed(2));
               const base = (leftBudget && leftBudget.budget_state_id === 2) ? a.total : ((rightBudget && rightBudget.budget_state_id === 2) ? b.total : Math.min(a.total, b.total));
               const pct = base > 0 ? Number((((a.total === base ? b.total : a.total) - base) / base * 100).toFixed(1)) : 0;
               const pctColor = pct === 0 ? undefined : (pct > 0 ? "#d32f2f" : "#2e7d32");
               const pctSign = pct > 0 ? "+" : "";
               const pctA = base > 0 ? Number((((a.total - base) / base) * 100).toFixed(1)) : 0;
               const pctB = base > 0 ? Number((((b.total - base) / base) * 100).toFixed(1)) : 0;
               const pctAColor = pctA === 0 ? undefined : (pctA > 0 ? "#d32f2f" : "#2e7d32");
               const pctBColor = pctB === 0 ? undefined : (pctB > 0 ? "#d32f2f" : "#2e7d32");
               const pctASign = pctA > 0 ? "+" : "";
               const pctBSign = pctB > 0 ? "+" : "";
              return (
                <TableRow key={name}>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>{name}</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>${a.workforce}</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>${a.materials}</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>
                    ${a.total}
                    <span style={{ marginLeft: 6 }}>
                      <Chip
                        size="small"
                        label={`${pctASign}${pctA}%`}
                        style={{ fontSize: "0.7rem", height: 20, color: pctAColor }}
                      />
                    </span>
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>&nbsp;</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>${b.workforce}</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>${b.materials}</TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem" }}>
                    ${b.total}
                    <span style={{ marginLeft: 6 }}>
                      <Chip
                        size="small"
                        label={`${pctBSign}${pctB}%`}
                        style={{ fontSize: "0.7rem", height: 20, color: pctBColor }}
                      />
                    </span>
                  </TableCell>
                  <TableCell className={classes.tableCell} style={{ fontSize: "0.75rem", color: diff === 0 ? undefined : (diff > 0 ? "#d32f2f" : "#2e7d32") }}>
                    ${diff}
                  </TableCell>
                   <TableCell className={classes.tableCell}>
                     <Chip
                       size="small"
                       label={`${pctSign}${pct}%`}
                       style={{ fontSize: "0.7rem", height: 20, color: pctColor }}
                     />
                   </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Dialog>
  );
};

export default withStyles(useStyles)(CompareBudgets);


